import {
  buildCombinedCode,
  conditionAssessment,
  type ConditionAssessmentInput,
  type ConditionId,
  type HabitatID,
  ukHabToMetric,
  ukHabToMetricMatches,
} from '@natural-apptitude/ukhab-lib';

/** Mapbox / tile paint keys (no spaces or dashes — safe in fill-pattern image ids). */
export type ConditionMapKey =
  | 'Poor'
  | 'FairlyPoor'
  | 'Moderate'
  | 'FairlyGood'
  | 'Good'
  | 'NA'
  | 'Other'
  | 'Empty';

export type BaselineMetricSummary = {
  distinctiv?: number;
  conResult?: ConditionMapKey;
};

type MappedData = Record<string, unknown>;

const CONDITION_ID_TO_MAP_KEY: Record<ConditionId, ConditionMapKey> = {
  good: 'Good',
  fairlyGood: 'FairlyGood',
  moderate: 'Moderate',
  fairlyPoor: 'FairlyPoor',
  poor: 'Poor',
  conditionAssessmentNA: 'NA',
  other: 'Other',
};

const CONDITION_DISPLAY_TO_MAP_KEY: Record<string, ConditionMapKey> = {
  Good: 'Good',
  'Fairly Good': 'FairlyGood',
  Moderate: 'Moderate',
  'Fairly Poor': 'FairlyPoor',
  Poor: 'Poor',
  'Condition Assessment N/A': 'NA',
  'N/A - Other': 'Other',
  'N/A': 'NA',
  NA: 'NA',
  Empty: 'Empty',
  FairlyGood: 'FairlyGood',
  FairlyPoor: 'FairlyPoor',
  Other: 'Other',
};

/**
 * Convert API / assessment condition strings (or ConditionIds) to map paint keys.
 */
export const conditionToMapKey = (value: string | null | undefined): ConditionMapKey | undefined => {
  if (value == null) {
    return undefined;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }
  const fromId = CONDITION_ID_TO_MAP_KEY[trimmed as ConditionId];
  if (fromId) {
    return fromId;
  }
  const fromDisplay = CONDITION_DISPLAY_TO_MAP_KEY[trimmed];
  if (fromDisplay) {
    return fromDisplay;
  }
  if (trimmed.includes('N/A')) {
    return 'NA';
  }
  return 'Other';
};

/** Export-path question keys that select a condition-assessment habitat. */
const habitatMapV2: [HabitatID, string][] = [
  ['coastal', 'coastalQ'],
  ['coastalLagoons', 'lagoonsQ'],
  ['coastalSaltmarsh', 'saltmarshQ'],
  ['ditch', 'ditchQ'],
  ['grasslandLow', 'lowgrassQ'],
  ['grasslandHigh', 'grassHighQ'],
  ['heathland', 'heathlandQ'],
  ['hedgerow', 'hedgeQ'],
  ['intertidalBiogenicReefs', 'reefsQ'],
  ['intertidalSeagrass', 'seagrassQ'],
  ['intertidalSediment', 'interSedQ'],
  ['intertidalHardStructures', 'structureQ'],
  ['individualTrees', 'treeQ'],
  ['lake', 'lakesQ'],
  ['limestonePavement', 'limestoneQ'],
  ['lineOfTrees', 'lineTreesQ'],
  ['orchard', 'orchardQ'],
  ['pond', 'pondsQ'],
  ['rockyShore', 'rockyShQ'],
  ['scrub', 'scrubQ'],
  ['sparselyVegetatedLand', 'sparseQ'],
  ['urban', 'urbanQ'],
  ['wetland', 'wetlandsQ'],
  ['woodland', 'woodlandQ'],
  ['woodpastureParkland', 'woodParkQ'],
];

export const getHabitatIdFromMappedData = (data: MappedData): HabitatID | undefined => {
  return habitatMapV2.find((hm) => !!data[hm[1]])?.[0];
};

const criteriaMap: Record<HabitatID, (string | null)[]> = {
  coastal: ['coastal1', 'coastal2', 'coastal3', 'coastal4', 'coastal5', 'coastal6', 'coastal7', 'coastal8'],
  rockyShore: ['rockySh1', 'rockySh2', 'rockySh3', 'rockySh4', 'rockySh5'],
  intertidalSediment: ['interSed1', 'interSed2', 'interSed3', 'interSed4', 'interSed5'],
  intertidalSeagrass: ['seagrass1', 'seagrass2', 'seagrass3', 'seagrass4', 'seagrass5'],
  intertidalBiogenicReefs: ['reefs1', 'reefs2', 'reefs3', 'reefs4', 'reefs5'],
  coastalSaltmarsh: ['saltmarsh1', 'saltmarsh2', 'saltmarsh3', 'saltmarsh4', 'saltmarsh5', 'saltmarsh6'],
  coastalLagoons: ['lagoons1', 'lagoons2', 'lagoons3', 'lagoons4', 'lagoons5', 'lagoons6', 'lagoons7', 'lagoons8'],
  hedgerow: ['hedge1', 'hedge2', 'hedge3', 'hedge4', 'hedge5', 'hedge6', 'hedge7', 'hedge8', 'hedge9', 'hedge10'],
  lake: ['lakes1', 'lakes2', 'lakes3', 'lakes4'],
  pond: ['ponds1', 'ponds2', 'ponds3', 'ponds4', 'ponds5', 'ponds6', 'ponds7', 'ponds8', 'ponds9'],
  scrub: ['scrub1', 'scrub2', 'scrub3', 'scrub4', 'scrub5'],
  grasslandLow: ['grassLow1', 'grassLow2', 'grassLow3', 'grassLow4', 'grassLow5', 'grassLow6', 'grassLow7'],
  grasslandHigh: ['grassHigh1', 'grassHigh2', 'grassHigh3', 'grassHigh4', 'grassHigh5', 'grassHigh6'],
  wetland: ['wetland1', 'wetland2', 'wetland3', 'wetland4', 'wetland5', 'wetland6', 'wetland7', 'wetland8', 'wetland9', 'wetland10'],
  heathland: ['heath1', 'heath2', 'heath3', 'heath4', 'heath5', 'heath6', 'heath7', 'heath8', 'heath9', 'heath10', 'heath11'],
  woodland: [
    'woodland1',
    'woodland2',
    'woodland3',
    'woodland4',
    'woodland5',
    'woodland6',
    'woodland7',
    'woodland8',
    'woodland9',
    'woodland10',
    'woodland11',
    'woodland12',
    'woodland13',
  ],
  lineOfTrees: ['lineTrees1', 'lineTrees2', 'lineTrees3', 'lineTrees4', 'lineTrees5'],
  individualTrees: ['tree1', 'tree2', 'tree3', 'tree4', 'tree5', 'tree6'],
  sparselyVegetatedLand: ['sparse1', 'sparse2', 'sparse3', 'sparse4'],
  urban: ['urban1', 'urban2', 'urban3', 'urban4', null, 'urban6', 'urban7', 'urban8', 'urban9', null, null, null, null, 'urbanX_3b'],
  limestonePavement: ['limestone1', 'limestone2', 'limestone3', 'limestone4', 'limestone5'],
  woodpastureParkland: ['woodPark1', 'woodPark2', 'woodPark3', 'woodPark4', 'woodPark5', 'woodPark6', 'woodPark7', 'woodPark8'],
  orchard: ['orchard1', 'orchard2', 'orchard3', 'orchard4', 'orchard5', 'orchard6', 'orchard7', 'orchard8'],
  ditch: ['ditch1', 'ditch2', 'ditch3', 'ditch4', 'ditch5', 'ditch6', 'ditch7', 'ditch8'],
  intertidalHardStructures: ['structure1', 'structure2', 'structure3', 'structure4', 'structure5'],
};

const assessConditionResult = (data: MappedData): string | undefined => {
  const habitatId = getHabitatIdFromMappedData(data);
  if (!habitatId) {
    return undefined;
  }
  const columns = criteriaMap[habitatId];
  const conCrit: Record<string, unknown> = {};
  for (let i = 0; i < columns.length; i++) {
    const key = columns[i];
    if (key === null) {
      continue;
    }
    conCrit[`conCrit${i + 1}`] = data[key];
  }
  if (habitatId === 'urban') {
    conCrit['conCritX'] = data['urbanX_3b'];
  }
  const input = {
    habitatId,
    metAddit: data['metAddit'],
    ukhabCCode: buildCombinedCode(data),
    ...conCrit,
  } as ConditionAssessmentInput;
  const assessment = conditionAssessment(input);
  return assessment.result ?? undefined;
};

/**
 * Distinctiveness + condition map keys from export-path-shaped mapped data
 * (API `mapData` or client `mapBaselineRawData`).
 */
export const computeBaselineMetricSummaryFromMappedData = (mapped: MappedData): BaselineMetricSummary => {
  const metChoice = mapped['metChoice'];
  const chosen =
    typeof metChoice === 'string' && metChoice.trim().length > 0
      ? ukHabToMetricMatches(buildCombinedCode(mapped)).find((m) => m.id === metChoice.trim())
      : undefined;

  const metAddit = mapped['metAddit'];
  const searchKey =
    typeof metAddit === 'string' && metAddit.trim().length > 0
      ? metAddit
      : buildCombinedCode(mapped);

  const metric = chosen ?? (searchKey.trim().length > 0 ? ukHabToMetric(searchKey) : undefined);
  const distinctivRaw = metric?.distinctiveness;
  const distinctiv =
    distinctivRaw !== undefined && distinctivRaw !== null && !Number.isNaN(Number(distinctivRaw))
      ? Number(distinctivRaw)
      : undefined;

  const assessed = assessConditionResult(mapped);
  const fromMetricCondition =
    metric?.condition !== undefined ? conditionToMapKey(metric.condition) : undefined;
  const conResult = conditionToMapKey(assessed) ?? fromMetricCondition;

  const out: BaselineMetricSummary = {};
  if (distinctiv !== undefined) {
    out.distinctiv = distinctiv;
  }
  if (conResult !== undefined) {
    out.conResult = conResult;
  }
  return out;
};
